import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Header } from "../../components/header/Header";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllContestThunk as onGetAllContestThunk,
  createContestThunk as onCreateContest,
  resetContestThunk as onResetContest,
  createParticipateContestThunk as onCreateParticipateContestThunk,
  getAllStatusesThunk as onGetAllStatusesThunk,
} from "../../slices/contest/thunk";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRole } from "../../utils/GetRole";
import toast from "react-hot-toast";
import { getAuthUser, getFileUrl } from "../../utils/GetAuthUser";
import { Spinner } from "../../components/spinner/Spinner";

const defaultImage = "/default.png";

export const Contests = () => {
  
  const fileUrl = getFileUrl();
  const role = useMemo(() => getRole(), []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { statusCode } = useParams();
  const authUser = useMemo(() => getAuthUser(), []);
  const [loadingContestId, setLoadingContestId] = useState(null);
  const [isContestSave, setIsContestSave] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusCode || 0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredContests, setFilteredContests] = useState([]);

  // Redux selector
  const selectContestState = (state) => state.contest;
  const contestSelector = createSelector(selectContestState, (contest) => ({
    isLoading: contest.isLoading,
    contestList: contest.contestList,
    isCreateContest: contest.isCreateContest,
    statusesList: contest.statusesList,
  }));

  const { contestList, isCreateContest, isLoading, statusesList } =
    useSelector(contestSelector);

  // ✅ Fetch contests with dynamic status & date
  const fetchContests = useCallback(async () => {
    const contestData = {
      statusCode: role == "artist" ? 0 : selectedStatus,
      artistId: null,
      is_publish: 1,
      date: selectedDate,

      checkParticipatArtistId:
        role == "artist" && authUser?.user?.id ? authUser.user.id : null,
        showRefferalContest: role == "artist" ? 2 : 1, // Only show referral contests to artists
    };

    dispatch(onResetContest());
   await dispatch(onGetAllContestThunk(contestData)).unwrap(); // waits for API success
  

  }, [dispatch, selectedStatus, selectedDate, authUser, role]);

  // ✅ Initial load (fetch statuses & contests)
  useEffect(() => {
    dispatch(onGetAllStatusesThunk());
    fetchContests();
  }, [dispatch, fetchContests]);

  // ✅ Re-fetch contests when status/date changes
  useEffect(() => {
    if (selectedStatus !== undefined) {
      fetchContests();
    }
  }, [selectedStatus, selectedDate, fetchContests]);

  // Handle contest creation success or error
  useEffect(() => {
    if (isCreateContest) {
      setIsContestSave(true);
    }
  }, [isCreateContest]);

  useEffect(() => {
    if (isContestSave) {
      toast.success("Contest created successfully");
      setLoadingContestId(null);
      fetchContests();
      setIsContestSave(false);
    }
  }, [isContestSave, fetchContests]);

  // Reset loading state when API call completes (success or error)
  useEffect(() => {
    if (!isLoading && loadingContestId) {
      setLoadingContestId(null);
    }
  }, [isLoading]);

  // Navigation & actions
  const handleNavigateToSketchDetail = useCallback(
    (contestId) => {
      navigate(`/${role}/contest-detail/2/${contestId}`);
    },
    [navigate, role]
  );

  const handleCreateContest = useCallback(
    (contestId) => {
      setLoadingContestId(contestId);
      dispatch(onCreateContest({ contestId }));
    },
    [dispatch]
  );

 const handleParticipate = useCallback(
  async (contestId) => {
    try {
      await dispatch(
        onCreateParticipateContestThunk({
          contestId,
          artistId: authUser?.user?.id || null,
          is_accept: 1,
        })
      ).unwrap();   // waits for API success

      // ✅ Refresh list after success
     fetchContests();

    } catch (error) {
      console.error("Participation failed:", error);
    }
  },
  [dispatch, authUser]
);

  // Page title
  const getPageTitle = useMemo(() => {
    if (selectedStatus == 0) return "All Contests";
    if (selectedStatus == 120) return "Requested Contests";
    return "Contests";
  }, [selectedStatus]);

  // Contest card
  const ContestCard = useCallback(
    ({ card }) => {
      const contestImage = card.contestImage
        ? `${fileUrl}${card.contestImage}`
        : defaultImage;

      return (
        <div
          key={card.id}
          className="w-full flex flex-col justify-between relative h-full p-3 border border-border_color rounded-xl"
        >
          {/* Contest Image */}
          <div className="w-full h-[175px]">
            <Link
              to="#"
              className="relative h-[175px] rounded-xl overflow-hidden block"
            >
              <img
                alt={card.title || "contest image"}
                className="object-cover object-center w-full h-full block rounded-xl"
                src={contestImage}
                onError={(e) => (e.currentTarget.src = defaultImage)}
              />
            </Link>
          </div>

          <span className="text-sm text-black font-medium font-grotesk absolute top-4 right-4 bg-white px-2 py-1 rounded-full shadow-md">
            {card.status || null} 
          </span>

          {/* Content */}
          <div className="flex flex-col justify-between flex-1 mt-3">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-center border border-[#DDDDDD] py-[6px] px-[14px] w-[130px] rounded-3xl">
                  <span className="font-bold text-[11px] text-[#262626] font-grotesk">
                    {card.title || card.contestCategory}
                  </span>
                </div>
              </div>
              <p className="text-[#696565] text-sm font-grotesk font-medium line-clamp-2">
                {card.description || card.contestDescription}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 w-full mt-4 flex-wrap">
              {(selectedStatus == 0 || selectedStatus == 140 || selectedStatus == 150) && (
                <button
                  className="flex-1 flex items-center justify-center text-xs font-bold border border-border_color font-grotesk text-primary py-3 px-5 rounded-[10px] whitespace-nowrap tracking-wide hover:text-white hover:bg-primary transition-all delay-75"
                  onClick={() => handleNavigateToSketchDetail(card.id)}
                >
                  View Details
                </button>
              )}

              {selectedStatus == 120 && (
                <button
                  className="flex-1 text-xs tracking-wide font-bold text-white font-grotesk bg-primary py-3 px-5 rounded-[10px] whitespace-nowrap flex items-center justify-center"
                  onClick={() => handleCreateContest(card.id)}
                  disabled={loadingContestId === card.id}
                >
                  {loadingContestId === card.id ? (
                    <>
                      <Spinner /> <span className="ml-2">Creating...</span>
                    </>
                  ) : (
                    "Create Contest"
                  )}
                </button>
              )}

              {selectedStatus == 140 && card.isAllowParticipate == 1 && (
                <button
                  className="flex-1 text-xs tracking-wide font-bold text-white font-grotesk bg-primary py-3 px-5 rounded-[10px] whitespace-nowrap"
                  onClick={() => handleParticipate(card.id)}
                >
                  Participate
                </button>
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      fileUrl,
      selectedStatus,
      handleNavigateToSketchDetail,
      handleCreateContest,
      handleParticipate,
    ]
  );

  // Skeleton card shown while contests are loading
  const SkeletonCard = () => (
    <div className="w-full flex flex-col justify-between relative h-full p-3 border border-border_color rounded-xl animate-pulse bg-white">
      <div className="w-full h-[175px] rounded-xl overflow-hidden bg-gray-200" />

      <span className="absolute top-4 right-4 w-20 h-6 bg-gray-200 rounded-full" />

      <div className="flex flex-col justify-between flex-1 mt-3">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="w-[130px] h-8 rounded-3xl bg-gray-200" />
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 w-full mt-4">
          <div className="h-10 bg-gray-200 rounded-[10px] w-full" />
        </div>
      </div>
    </div>
  );


  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header />
      </header>

      <div className="w-full mx-auto bg-[#F7F7F7]">
        <div className="w-full">
          {/* Page Title */}
          <div className="w-full flex items-center justify-between mt-4 flex-wrap gap-2">
            <span className="font-bold text-black text-[22px] font-grotesk">
              {getPageTitle}
            </span>

            <span className="flex gap-3">
              {/* Date Filter */}
              <input
                type="date"
                value={selectedDate || ""}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full max-w-[120px] xs:max-w-[140px] px-3 py-3 bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />

              {/* Dynamic Status Dropdown */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full min-w-[140px]  px-3 py-3 bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary xs:min-w-36"
            >
              <option value={0}>All</option>

              {statusesList?.length > 0 &&
                statusesList.map((status) => {
                  return (
                    <option key={status.id} value={status.status_code}>
                      {status.status}
                    </option>
                  );
                })}
            </select>

            </span>
          </div>

          {/* Contest Grid */}
          <div className="w-full mt-6">
            {isLoading ? (
              <div className="w-full mx-auto grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {Array.from({ length: 10 }).map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))}
              </div>
            ) : (
              <div className="w-full mx-auto grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {contestList && contestList.length > 0 ? (
                  contestList.map((card) => (
                    <ContestCard key={card.id} card={card} />
                  ))
                ) : (
                  <p className="text-center text-gray-500">No contests found</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
